import { ConfirmDialog } from "@/components/ConfirmDialog";
import { GithubIcon } from "@/components/GithubIcon";
import { GitlabIcon } from "@/components/GitlabIcon";
import { HostIcon } from "@/components/HostIcon";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ApiError, api, type CodeHostConnection, type CodeHostKind } from "@/lib/api";
import { containerPermissions } from "@/lib/boo-permissions";
import { codeHostOutcomeMessage, hostLabel, instanceLabel, leaveForHost } from "@/lib/code-host";
import { GitLabForm } from "@/pages/container-tabs/connections/CodeHostsCard";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Loader2, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

// One host's workspace connections: any Boo can be given them. Owners and
// admins connect them here (or share one from a Boo's Connections), choose
// which Boos use each one, and remove them.
export function WorkspaceCodeHostsSection({ host }: { host: CodeHostKind }) {
  const queryClient = useQueryClient();
  const hosts = useQuery({ queryKey: ["code-hosts", "workspace"], queryFn: () => api.codeHosts.list() });
  const boos = useQuery({ queryKey: ["containers"], queryFn: api.containers.list });
  const [removing, setRemoving] = useState<CodeHostConnection | null>(null);
  const [gitlabOpen, setGitlabOpen] = useState(false);
  const returnTo = typeof window === "undefined" ? "/" : window.location.pathname;
  const github = useMutation({
    mutationFn: async () => {
      const { url } = await api.codeHosts.githubInstall(undefined, "workspace", returnTo);
      leaveForHost(url, "github.com");
    },
  });
  // Back from GitHub or GitLab: say how it went, then clean the address.
  useEffect(() => {
    const url = new URL(window.location.href);
    const outcome = url.searchParams.get("codehost");
    if (!outcome) return;
    const { kind, text } = codeHostOutcomeMessage(outcome);
    if (kind === "success") toast.success(text);
    else if (kind === "error") toast.error(text);
    else toast(text);
    queryClient.invalidateQueries({ queryKey: ["code-hosts"] });
    url.searchParams.delete("codehost");
    window.history.replaceState(window.history.state, "", url.pathname + (url.search || ""));
  }, [queryClient]);
  const remove = useMutation({
    mutationFn: (id: string) => api.codeHosts.remove(id),
    onSuccess: () => {
      setRemoving(null);
      queryClient.invalidateQueries({ queryKey: ["code-hosts"] });
    },
  });
  if (hosts.isError && hosts.error instanceof ApiError && hosts.error.status === 403) return null;
  const pool = (hosts.data?.workspace ?? []).filter((c) => c.host === host && c.status !== "revoked");
  const booName = (id: string) => boos.data?.data.find((b) => b.id === id)?.name ?? "a Boo";
  const connectButtons = (
    <div className="flex shrink-0 items-center gap-2">
      {host === "github" ? (
        <Button
          size="sm"
          variant="outline"
          disabled={!hosts.data?.githubAvailable || github.isPending}
          title={hosts.data?.githubAvailable === false ? "GitHub isn't set up on this backend yet." : undefined}
          onClick={() => github.mutate()}
        >
          {github.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <GithubIcon className="size-3.5" />}
          Connect GitHub
        </Button>
      ) : (
        <Button size="sm" variant="outline" onClick={() => setGitlabOpen(true)}>
          <GitlabIcon className="size-3.5" />
          Connect GitLab
        </Button>
      )}
    </div>
  );
  return (
    <section className="space-y-3">
      {hosts.isError && <InlineError error={hosts.error} />}
      {github.isError && <InlineError error={github.error} />}
      {remove.isError && <InlineError error={remove.error} />}
      {hosts.isLoading ? (
        <div className="inline-flex items-center gap-2 rounded-lg border p-4 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" /> Checking…
        </div>
      ) : pool.length === 0 ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border p-4">
          <div className="flex flex-col gap-0.5">
            <div className="text-sm font-medium">No workspace {hostLabel(host)} connection yet</div>
            <div className="text-xs text-muted-foreground">Connect one here to share it with your Boos, or from a Boo&apos;s Connections to keep it to that Boo.</div>
          </div>
          {connectButtons}
        </div>
      ) : (
        <div className="rounded-lg border">
          <ul className="divide-y">
            {pool.map((conn) => (
              <li key={conn.id} className="flex items-center gap-3 p-4">
                <HostIcon host={conn.host} className="size-4 shrink-0" />
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div className="flex flex-wrap items-center gap-x-2">
                    <span className="truncate text-sm font-medium">{conn.namespace || "Choosing a group…"}</span>
                    {instanceLabel(conn) && <span className="text-xs text-muted-foreground">{instanceLabel(conn)}</span>}
                    {conn.status === "suspended" && <span className="text-xs text-amber-700 dark:text-amber-300">Suspended on GitHub</span>}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {conn.booIds?.length ? `Used by ${conn.booIds.map(booName).join(", ")}` : "Not used by any Boo yet"}
                  </div>
                </div>
                {conn.status === "active" && boos.data && (
                  <BooAccessMenu connection={conn} boos={boos.data} onChanged={() => queryClient.invalidateQueries({ queryKey: ["code-hosts"] })} />
                )}
                {conn.manageUrl && (
                  <a href={conn.manageUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-muted-foreground underline-offset-4 hover:underline">
                    Manage <ExternalLink className="size-3" />
                  </a>
                )}
                <Button size="sm" variant="ghost" className="text-muted-foreground" aria-label={`Remove ${conn.namespace}`} onClick={() => setRemoving(conn)}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-4 py-3">{connectButtons}</div>
        </div>
      )}
      <Dialog open={gitlabOpen} onOpenChange={setGitlabOpen}>
        <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-md" showCloseButton={false}>
          {gitlabOpen && (
            <GitLabForm
              scope="workspace"
              gitlabOAuthAvailable={hosts.data?.gitlabOAuthAvailable ?? false}
              returnTo={returnTo}
              onCancel={() => setGitlabOpen(false)}
              onConnected={() => {
                setGitlabOpen(false);
                queryClient.invalidateQueries({ queryKey: ["code-hosts"] });
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(null)}
        title={`Remove ${removing?.namespace ?? "this connection"} from the workspace?`}
        description="Every Boo using it loses access and the stored credential is forgotten. Nothing changes on the host."
        confirmLabel="Remove"
        loading={remove.isPending}
        onConfirm={() => removing && remove.mutate(removing.id)}
      />
    </section>
  );
}

// Which Boos use one workspace code host connection. Giving or taking it
// needs admin rights on that Boo, so Boos the viewer can't configure are
// listed but disabled.
function BooAccessMenu({
  connection,
  boos,
  onChanged,
}: {
  connection: CodeHostConnection;
  boos: Awaited<ReturnType<typeof api.containers.list>>;
  onChanged: () => void;
}) {
  const granted = new Set(connection.booIds ?? []);
  const toggle = useMutation({
    mutationFn: ({ containerId, on }: { containerId: string; on: boolean }) =>
      on ? api.codeHosts.grant(connection.id, containerId) : api.codeHosts.revokeGrant(connection.id, containerId),
    onSuccess: onChanged,
    onError: (err) => toast.error(err instanceof Error ? err.message : "Couldn't change access"),
  });
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="gap-1.5" disabled={toggle.isPending}>
          {toggle.isPending ? <Loader2 className="size-3.5 animate-spin" /> : <Users className="size-3.5" />}
          Boos
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          Boos that use {connection.namespace}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {boos.data.map((boo) => {
          const canConfigure = containerPermissions(boo, boos._permissions)?.canEdit === true;
          return (
            <DropdownMenuCheckboxItem
              key={boo.id}
              checked={granted.has(boo.id)}
              disabled={!canConfigure}
              onSelect={(e) => e.preventDefault()}
              onCheckedChange={(on) => toggle.mutate({ containerId: boo.id, on })}
            >
              <span className="truncate">{boo.name}</span>
            </DropdownMenuCheckboxItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
